import { useCallback, useRef, useState } from 'react';

export function useListSelection<T>(getKey: (item: T) => string | undefined) {
  const [selectedItems, setSelectedItems] = useState<T[]>([]);
  const lastIndexRef = useRef<number>(-1);

  const handleSelect = useCallback(
    (item: T, items: T[], event?: { shiftKey: boolean }) => {
      const key = getKey(item);
      const currentIndex = items.findIndex((i) => getKey(i) === key);

      setSelectedItems((prev) => {
        const isAlreadySelected = prev.some((i) => getKey(i) === key);

        if (event?.shiftKey && lastIndexRef.current >= 0) {
          const start = Math.min(lastIndexRef.current, currentIndex);
          const end = Math.max(lastIndexRef.current, currentIndex);
          const range = items.slice(start, end + 1);
          const merged = [...prev];
          range.forEach((r) => {
            if (!merged.some((i) => getKey(i) === getKey(r))) merged.push(r);
          });
          return merged;
        }

        if (isAlreadySelected) return [];
        return [item];
      });

      if (!event?.shiftKey) lastIndexRef.current = currentIndex;
    },
    [getKey],
  );

  const clearSelection = useCallback(() => {
    setSelectedItems([]);
    lastIndexRef.current = -1;
  }, []);

  return { selectedItems, setSelectedItems, handleSelect, clearSelection };
}
